import {Nullable, RatingDistributionType, RatingStatisticsType} from "../../definitions/types";
import {RatingDistributionModel} from "./ratingDistributionModel";
import {defaultRatingStatistics} from "../../utils/constants";

export class RatingStatisticsModel {
    private readonly _count: number;
    private readonly _sum: number;
    private readonly _min: number;
    private readonly _max: number;
    private readonly _range: number;
    private readonly _mean: number;
    private readonly _median: number;
    private readonly _mode: number;
    private readonly _standardDeviation: number;
    private readonly _ratingDistributionType: RatingDistributionModel;

    constructor(public readonly ratingStatistics: Nullable<RatingStatisticsType>) {
        const {
            count,
            sum,
            min,
            max,
            range,
            mean,
            median,
            mode,
            standardDeviation,
            distribution,
        } = ratingStatistics ?? defaultRatingStatistics;
        this._count = count;
        this._sum = sum;
        this._min = min;
        this._max = max;
        this._range = range;
        this._mean = mean
        this._median = median;
        this._mode = mode;
        this._standardDeviation = standardDeviation;
        this._ratingDistributionType = new RatingDistributionModel(distribution);
    }

    /**
     * @method count
     * @description Returns the number of ratings for the product.
     * @returns {number}
     */
    public get count(): number {
        return this._count;
    }

    /**
     * @method sum
     * @description Returns the sum of all ratings for the product.
     * @returns {number}
     */
    public get sum(): number {
        return this._sum;
    }

    /**
     * @method min
     * @description Returns the lowest rating for the product.
     * @returns {number}
     */
    public get min(): number {
        return this._min;
    }

    /**
     * @method max
     * @description Returns the highest rating for the product.
     * @returns {number}
     */
    public get max(): number {
        return this._max;
    }

    /**
     * @method range
     * @description Returns the range of ratings for the product.
     * @returns {number}
     */
    public get range(): number {
        return this._range;
    }

    /**
     * @method median
     * @description Returns the median rating for the product.
     * @returns {number}
     */
    public get median(): number {
        return this._median;
    }

    /**
     * @method mode
     * @description Returns the mode rating for the product.
     * @returns {number}
     */
    public get mode(): number {
        return this._mode;
    }

    /**
     * @method mean
     * @description Returns the mean rating for the product.
     * @returns {number}
     */
    public get mean(): number {
        return this._mean;
    }

    /**
     * @method standardDeviation
     * @description Returns the standard deviation of the ratings for the product.
     * @returns {number}
     */
    public get standardDeviation(): number {
        return this._standardDeviation;
    }

    /**
     * @method distribution
     * @description Returns the rating distribution of the product.
     * @returns {RatingDistributionModel}
     */
    public get distribution(): RatingDistributionModel {
        return this._ratingDistributionType;
    }
}